import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface Range {
  id: string;
  code: string;
  name: string;
  period_type: string;
}
interface Period {
  month: number;
  cutoffFromDay: string;
  cutoffToDay: string;
  midCutoffFromDay: string;
  midCutoffToDay: string;
}
interface Employee {
  id: string;
  code: string;
  first_name: string;
  last_name: string;
}

const EMPTY = { code: '', name: '', periodType: 'END_MONTH' };
const emptyPeriods = (): Period[] =>
  Array.from({ length: 12 }, (_, i) => ({ month: i + 1, cutoffFromDay: '', cutoffToDay: '', midCutoffFromDay: '', midCutoffToDay: '' }));

export default function ClaimDateRanges() {
  const [ranges, setRanges] = useState<Range[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<Range | null>(null);
  const [error, setError] = useState('');

  const [periodsTarget, setPeriodsTarget] = useState<Range | null>(null);
  const [periods, setPeriods] = useState<Period[]>(emptyPeriods());

  const [assignTarget, setAssignTarget] = useState<Range | null>(null);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [pickEmployeeIds, setPickEmployeeIds] = useState<string[]>([]);

  const load = async () => {
    const res = await api.get('/ecs/masters/claim-date-ranges');
    setRanges(res.data.claimDateRanges || []);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setError('');
    setShowForm(true);
  };

  const openEdit = (row: Range) => {
    setEditingId(row.id);
    setForm({ code: row.code, name: row.name, periodType: row.period_type });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) await api.put(`/ecs/masters/claim-date-ranges/${editingId}`, form);
      else await api.post('/ecs/masters/claim-date-ranges', form);
      setShowForm(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/ecs/masters/claim-date-ranges/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  const openPeriods = async (row: Range) => {
    setPeriodsTarget(row);
    const res = await api.get(`/ecs/masters/claim-date-ranges/${row.id}/periods`);
    const existing = res.data.periods || [];
    setPeriods(
      emptyPeriods().map((p) => {
        const found = existing.find((e: any) => e.month === p.month);
        return found
          ? {
              month: p.month,
              cutoffFromDay: String(found.cutoff_from_day ?? ''),
              cutoffToDay: String(found.cutoff_to_day ?? ''),
              midCutoffFromDay: String(found.mid_cutoff_from_day ?? ''),
              midCutoffToDay: String(found.mid_cutoff_to_day ?? ''),
            }
          : p;
      })
    );
  };

  const copyToAll = () => {
    setPeriods((prev) => prev.map((p, i) => (i === 0 ? p : { ...prev[0], month: p.month })));
  };

  const savePeriods = async () => {
    if (!periodsTarget) return;
    await api.put(`/ecs/masters/claim-date-ranges/${periodsTarget.id}/periods`, { periods });
    setPeriodsTarget(null);
  };

  const openAssign = async (row: Range) => {
    setAssignTarget(row);
    setPickEmployeeIds([]);
    const res = await api.get('/employees');
    setAllEmployees(res.data.employees || []);
  };

  const saveAssign = async () => {
    if (!assignTarget || !pickEmployeeIds.length) return;
    await api.post(`/ecs/masters/claim-date-ranges/${assignTarget.id}/assign`, { employeeIds: pickEmployeeIds });
    setAssignTarget(null);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Claim Date Range</h1>
        <Button onClick={openCreate}>+ New</Button>
      </div>
      <Table
        columns={[
          { key: 'code', header: 'Code' },
          { key: 'name', header: 'Name' },
          { key: 'period_type', header: 'Period Type' },
          {
            key: 'actions',
            header: 'Actions',
            render: (row) => (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => openEdit(row)}>
                  Edit
                </Button>
                <Button variant="ghost" onClick={() => openPeriods(row)}>
                  Cut-offs
                </Button>
                <Button variant="ghost" onClick={() => openAssign(row)}>
                  Assign
                </Button>
                <Button variant="ghost" className="text-red-600" onClick={() => setDeleteTarget(row)}>
                  Delete
                </Button>
              </div>
            ),
          },
        ]}
        rows={ranges}
      />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Claim Date Range' : 'New Claim Date Range'}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input label="Name" maxLength={50} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Select
            label="Period Type"
            options={[
              { value: 'END_MONTH', label: 'End Month Only' },
              { value: 'MID_END_MONTH', label: 'Mid + End Month' },
            ]}
            value={form.periodType}
            onChange={(e) => setForm({ ...form, periodType: e.target.value })}
          />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!periodsTarget} onClose={() => setPeriodsTarget(null)} title={`Cut-off Dates — ${periodsTarget?.name}`}>
        <div className="flex flex-col gap-3">
          <Button variant="secondary" onClick={copyToAll}>
            Copy Row 1 to all rows
          </Button>
          <div className="overflow-x-auto">
            <table className="text-sm">
              <thead>
                <tr>
                  <th className="p-1 text-left">Month</th>
                  <th className="p-1 text-left">Cutoff From (day)</th>
                  <th className="p-1 text-left">Cutoff To (day)</th>
                  <th className="p-1 text-left">Mid From</th>
                  <th className="p-1 text-left">Mid To</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p, idx) => (
                  <tr key={p.month}>
                    <td className="p-1">{MONTHS[p.month - 1]}</td>
                    <td className="p-1"><input type="number" min={1} max={31} className="border rounded px-1 py-0.5 w-16" value={p.cutoffFromDay} onChange={(e) => setPeriods((prev) => prev.map((x, i) => (i === idx ? { ...x, cutoffFromDay: e.target.value } : x)))} /></td>
                    <td className="p-1"><input type="number" min={1} max={31} className="border rounded px-1 py-0.5 w-16" value={p.cutoffToDay} onChange={(e) => setPeriods((prev) => prev.map((x, i) => (i === idx ? { ...x, cutoffToDay: e.target.value } : x)))} /></td>
                    <td className="p-1"><input type="number" min={1} max={31} className="border rounded px-1 py-0.5 w-16" value={p.midCutoffFromDay} onChange={(e) => setPeriods((prev) => prev.map((x, i) => (i === idx ? { ...x, midCutoffFromDay: e.target.value } : x)))} /></td>
                    <td className="p-1"><input type="number" min={1} max={31} className="border rounded px-1 py-0.5 w-16" value={p.midCutoffToDay} onChange={(e) => setPeriods((prev) => prev.map((x, i) => (i === idx ? { ...x, midCutoffToDay: e.target.value } : x)))} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button onClick={savePeriods}>Save Cut-off Dates</Button>
        </div>
      </Modal>

      <Modal isOpen={!!assignTarget} onClose={() => setAssignTarget(null)} title={`Assign Employees — ${assignTarget?.name}`}>
        <div className="flex flex-col gap-4">
          <select
            multiple
            className="border rounded-lg p-2 h-40"
            value={pickEmployeeIds}
            onChange={(e) => setPickEmployeeIds(Array.from(e.target.selectedOptions).map((o) => o.value))}
          >
            {allEmployees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.code} — {emp.first_name} {emp.last_name}
              </option>
            ))}
          </select>
          <Button onClick={saveAssign}>Assign Selected</Button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        message={`Delete claim date range "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
